import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';

/*
 * Small building blocks shared by the order detail cards.
 *
 * Everything here draws on the brand tokens defined in App.css rather than
 * Tailwind's stock `slate` ramp, which is what made this screen look like a
 * different product from the rest of the admin.
 */

export const Card = ({ children, className = '' }) => (
  <section
    className={`bg-white rounded-xl border border-[#D6DFE2] ${className}`}
  >
    {children}
  </section>
);

export const CardHeader = ({ title, action }) => (
  <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 border-b border-[#E1E7EA]">
    <h2 className="text-[13px] font-bold tracking-[0.01em] text-nysmah-dark m-0">
      {title}
    </h2>
    {action}
  </div>
);

export const CardBody = ({ children, className = '' }) => (
  <div className={`px-3.5 py-3 ${className}`}>{children}</div>
);

/** Tinted strip at the foot of a card, for actions that mutate that card's data. */
export const CardFooter = ({ children }) => (
  <div className="px-3.5 py-2.5 border-t border-[#E1E7EA] bg-[#F5F8F8] rounded-b-xl flex items-center gap-2 flex-wrap">
    {children}
  </div>
);

export const Label = ({ children, className = '' }) => (
  <span
    className={`text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#6B7C86] ${className}`}
  >
    {children}
  </span>
);

/** Label / value row used inside the rail cards. */
export const Row = ({ label, children, action }) => (
  <div className="flex items-baseline justify-between gap-3 py-1">
    <span className="text-[12px] font-medium text-[#6B7C86] shrink-0">{label}</span>
    <span className="flex items-center gap-1 min-w-0">
      <span className="text-[13px] font-semibold text-nysmah-dark text-right break-words">
        {children}
      </span>
      {action}
    </span>
  </div>
);

/** Absent values read as absent, not as a rendering failure. */
export const Empty = ({ children = '—' }) => (
  <span className="text-nysmah-muted font-normal">{children}</span>
);

export const Button = ({
  variant = 'default',
  size = 'md',
  className = '',
  children,
  ...props
}) => {
  const base =
    'inline-flex items-center justify-center gap-1.5 font-semibold rounded-lg border transition-colors whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-nysmah-accent focus-visible:ring-offset-1 disabled:opacity-50 disabled:pointer-events-none';
  const variants = {
    default:
      'bg-white border-[#E1E7EA] text-[#4A5B66] hover:bg-[#F5F8F8] hover:text-nysmah-dark',
    primary:
      'bg-nysmah-accent border-nysmah-accent text-white hover:bg-[#426C7E]',
    ghost:
      'bg-transparent border-transparent text-nysmah-muted hover:text-nysmah-dark hover:bg-[#F0F4F5]',
    danger:
      'bg-white border-[#EFC4C4] text-[#C2453D] hover:bg-[#FCF2F2]',
  };
  const sizes = {
    sm: 'text-[12px] px-2.5 py-1.5',
    md: 'text-[13px] px-3.5 py-2',
    icon: 'p-1.5',
  };

  return (
    <button
      type="button"
      className={`${base} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

/**
 * Copy-to-clipboard for the values operators retype constantly — order number,
 * phone, barcode, tracking number. Falls back silently where the Clipboard API is
 * unavailable (non-secure origins) rather than throwing into the console.
 */
export const CopyButton = ({ value, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleCopy = useCallback(async () => {
    if (!value || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard blocked — the value is still on screen to select by hand.
    }
  }, [value]);

  if (!value) return null;

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? 'Copied' : label}
      title={copied ? 'Copied' : label}
      className="inline-flex items-center justify-center p-1 rounded text-nysmah-muted hover:text-nysmah-accent hover:bg-[#F0F4F5] bg-transparent border-0 cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-nysmah-accent"
    >
      {copied ? (
        <Check className="w-3 h-3 text-nysmah-success" aria-hidden="true" />
      ) : (
        <Copy className="w-3 h-3" aria-hidden="true" />
      )}
    </button>
  );
};
